/**
 * trackOperations — ЕДИНЫЙ источник правды: какие операции над треком реально
 * доступны какому провайдеру (Suno / Mureka) и бесплатна ли операция, если
 * результат уже готов. Все три UI (поповер действий SunoActionsSheet, кабинет
 * TrackCabinet, виджет) читают ЭТОТ файл — без локальных `if (isMureka)`.
 * Добавил операцию здесь → она корректно появляется/прячется/ценится во всех UI.
 *
 * Сверено с БЭКЕНДОМ (authoritative API-истина):
 *  • Блок Mureka — process.py: music_cover, mashup, boost_style
 *    («Операция недоступна для модели Mureka»).
 *  • timestamped_lyrics — для Mureka ДОСТУПНО и БЕСПЛАТНО: пословные тайминги
 *    приходят в ответе генерации (lyrics_sections → lines → words, мс) и уже
 *    сохранены у нас; эндпоинт /timestamped-lyrics собирает их без вызова API
 *    (_mureka_timestamped_from_generation). Токены не списываются.
 *  • boost_style — также boost_style.py:49 («недоступно для Mureka»).
 *  • Mureka-only: describe_song (process.py:2625), soundtrack (generate.py:228),
 *    reference (process.py:3564).
 *  • replace_section — бэк-код для Mureka ЕСТЬ (process.py:2454), но живой Mureka
 *    API отвергает («Invalid Request», не дорешено) → здесь suno-only, как сейчас
 *    во фронте. Когда живой кейс починят — добавить 'mureka' в providers.
 *  • wav_convert — если wav_url уже есть, сервер рефандит (process.py:2238 Suno /
 *    2504 Mureka) → у нас бесплатно (просто скачать), цену не показываем.
 */
// elevenlabs — не музыка, а озвучка текста: из операций у неё только 'tts'.
export type MusicProvider = 'suno' | 'mureka' | 'elevenlabs'

interface TrackLike {
	wav_url?: string | null
	/** Нужен там, где бесплатность зависит от провайдера (караоке у Mureka). */
	model?: string | null
}

export interface TrackOpDef {
	/** Ключ = variant-ключ для priceOf (и строка операции бэка). */
	key: string
	/** Провайдеры, у которых операция РЕАЛЬНО доступна (сверено с бэком). */
	providers: MusicProvider[]
	/** Бесплатно, если результат уже готов (напр. WAV уже сконвертирован). */
	freeIf?: (track: TrackLike) => boolean
}

export const TRACK_OPS: Record<string, TrackOpDef> = {
	// ── доступны обоим провайдерам ──
	extend: { key: 'extend', providers: ['suno', 'mureka'] },
	upload_extend: { key: 'upload_extend', providers: ['suno', 'mureka'] },
	upload_cover: { key: 'upload_cover', providers: ['suno', 'mureka'] },
	add_vocals: { key: 'add_vocals', providers: ['suno', 'mureka'] },
	add_instrumental: { key: 'add_instrumental', providers: ['suno', 'mureka'] },
	stem_separation: { key: 'stem_separation', providers: ['suno', 'mureka'] },
	// Минусовка — голос и минус двумя файлами. Умеют оба: у Suno это тип
	// separate_vocal, у Mureka модель audio-separation-3 (выбирает бэкенд).
	// Ключ цены свой; если он не заведён, бэк считает по полному разбору.
	stem_minus: { key: 'stem_minus', providers: ['suno', 'mureka'] },
	midi: { key: 'midi', providers: ['suno', 'mureka'] },
	music_video: { key: 'music_video', providers: ['suno', 'mureka'] },
	// Клип по треку — движок Mureka (их «Music Video» поверх Seedance), но
	// трек ЛЮБОЙ: Suno-трек уходит url-источником (их API: song_id | url).
	// Субтитры лирики — только у Mureka-треков (текст они знают по song_id).
	mureka_clip: { key: 'mureka_clip', providers: ['suno', 'mureka'] },
	persona: { key: 'persona', providers: ['suno', 'mureka'] },
	wav_convert: { key: 'wav_convert', providers: ['suno', 'mureka'], freeIf: (t) => !!t.wav_url },
	// Караоке: у Suno — платная align-операция, у Mureka пословные тайминги
	// приходят вместе с генерацией и лежат у нас → бесплатно, цену не показываем.
	timestamped_lyrics: {
		key: 'timestamped_lyrics',
		providers: ['suno', 'mureka'],
		freeIf: (t) => providerOf(t.model) === 'mureka',
	},
	// ── только Suno (Mureka блокирован на бэке либо живой API не тянет) ──
	mashup: { key: 'mashup', providers: ['suno'] },
	music_cover: { key: 'music_cover', providers: ['suno'] },
	boost_style: { key: 'boost_style', providers: ['suno'] },
	replace_section: { key: 'replace_section', providers: ['suno'] },
	// Партитура (PDF + MusicXML). Движок Mureka, но работает по ссылке на
	// аудио — значит доступна и трекам Suno, и загруженным файлам.
	transcribe: { key: 'transcribe', providers: ['suno', 'mureka'] },
	// ── только Mureka ──
	describe_song: { key: 'describe_song', providers: ['mureka'] },
	soundtrack: { key: 'soundtrack', providers: ['mureka'] },
	// Озвучка на странице «Озвучка» — ElevenLabs (с 02.10.2026 вместо Mureka:
	// русский и 90+ языков). Ручка Mureka /music/tts на бэке осталась, но из
	// интерфейса её больше не зовут.
	tts: { key: 'tts', providers: ['elevenlabs'] },
	reference: { key: 'reference', providers: ['mureka'] },
}

/** Провайдер по id модели трека (1:1 с логикой во фронте). */
export function providerOf(modelId: string | null | undefined): MusicProvider {
	const id = (modelId || '').toLowerCase()
	if (id.startsWith('mureka')) return 'mureka'
	if (id.startsWith('elevenlabs')) return 'elevenlabs'
	return 'suno'
}

/** Доступна ли операция провайдеру. Неизвестный ключ → true (не ломаем legacy). */
export function opAvailable(key: string, provider: MusicProvider): boolean {
	const op = TRACK_OPS[key]
	return op ? op.providers.includes(provider) : true
}

/** Бесплатна ли операция прямо сейчас (результат уже готов). */
export function opFree(key: string, track: TrackLike): boolean {
	const op = TRACK_OPS[key]
	return op?.freeIf ? op.freeIf(track) : false
}
